"use client";

import Link from "next/link";
import { useActionState, useEffect, useRef, useState } from "react";
import { getPostApplyOfferState } from "@/app/jobs/[id]/offer-actions";
import {
  subscribeToJobAlerts,
  type SubscribeActionResult,
} from "@/app/job-alerts/actions";
import { FeedbackLink } from "@/components/FeedbackLink";
import { HoneypotField } from "@/components/HoneypotField";
import { MicroSurvey, shouldAskExitSurvey } from "@/components/MicroSurvey";
import { capture } from "@/lib/analytics/events";
import {
  PREMIUM_BILLING_NOTE,
  PREMIUM_PRICE_LABEL,
} from "@/lib/portfolio/entitlements";

/**
 * What we say to someone the moment they come back from an employer's ATS.
 *
 * That return is the only point on the site where intent is PROVEN — they did
 * not browse, they applied. Two tickets landed on it from opposite directions:
 *
 *  - T-246: 363 people were shown the offer in a week and 0 opened checkout.
 *    Most of those views were the old post-apply CTA, which pitched "track it
 *    in your portfolio" — about us, not about what they just did.
 *  - T-247: 677 people clicked apply in a week and 9 left an address (0.3%).
 *
 * So this box makes one ask — a video application tailored to the job the
 * reader just applied to, with the price on the same screen — and, for anyone
 * that isn't for, an inline free alert for this exact search. That form was
 * briefly replaced by a link (2026-09-30) on the belief that it converted
 * ~20x worse than `JobAlertCapture`; the real cause was that its checkbox
 * sent terms=1 and `subscribeToJobAlerts`' predecessor only accepted "on", so
 * every submit was refused. Change Maker's identical form converts 3.6%.
 *
 * It replaces PostApplySurvey, whose yes/no question sat at 0 responses
 * against 71 tab-returns. It keeps that component's two hard-won mechanics,
 * both of which cost a run each to find:
 *  - reveal on the hidden->visible transition, never on the click, because
 *    `target=_blank` means the click happens in a tab nobody is looking at;
 *  - scroll with `behavior: "instant"`, because rAF-driven smooth scrolling is
 *    throttled at exactly that transition and the box never arrived.
 */
export function PostApplyPanel({
  jobId,
  orgKey,
  jobTitle,
  orgName,
  applyUrl,
  alertFilters,
  alertScope,
}: {
  jobId: number;
  orgKey: string;
  jobTitle: string;
  orgName: string;
  applyUrl: string;
  /** Alert-scope querystring for "more jobs like this one". */
  alertFilters: string;
  /** Human summary of "more jobs like this one", shown so the offer is concrete. */
  alertScope: string;
}) {
  const [alertResult, alertAction, alertPending] = useActionState<
    SubscribeActionResult | null,
    FormData
  >(subscribeToJobAlerts, null);
  const captured = alertResult?.ok === true;
  useEffect(() => {
    if (!captured) return;
    capture("post_apply_alert_captured", {
      job_id: jobId,
      state: alertResult?.alreadyConfirmed ? "already_on" : "check_inbox",
    });
  }, [captured, alertResult?.alreadyConfirmed, jobId]);
  const [applied, setApplied] = useState(false);
  const [askHesitation, setAskHesitation] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const returnedRef = useRef(false);
  // Members are not sold what they already have, and must not be counted in
  // premium_pitch_view — that number is the whole point of CM-439. The page is
  // ISR-cached and cannot know, so this is asked from the client on reveal.
  const [isPaid, setIsPaid] = useState(false);
  // Read at capture time, not through the effect's closure: the visibility
  // return can beat the lookup, and a stale `false` there would put members
  // back into the impression count this whole change exists to make honest.
  const isPaidRef = useRef(false);

  useEffect(() => {
    function onApplyClick() {
      setApplied(true);
    }
    window.addEventListener("finseats:apply_click", onApplyClick);
    return () => window.removeEventListener("finseats:apply_click", onApplyClick);
  }, []);

  useEffect(() => {
    if (!applied) return;
    let alive = true;
    getPostApplyOfferState()
      .then((s) => {
        if (!alive) return;
        isPaidRef.current = s.isPaid;
        setIsPaid(s.isPaid);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [applied]);

  useEffect(() => {
    if (!applied) return;
    returnedRef.current = false;

    function onVisibility() {
      if (document.visibilityState !== "visible" || returnedRef.current) return;
      returnedRef.current = true;
      capture("post_apply_tab_return", { job_id: jobId, org_key: orgKey });
      if (!isPaidRef.current) {
        capture("premium_pitch_view", { surface: "job_post_apply" });
        // Dwell-based ask, ported from the dashboard paywall (T-284) to the
        // surface that actually gets traffic (~900 views/30d vs dashboard's
        // near-zero). The two click-triggered FeedbackLinks below only reach
        // someone who notices a small link; this reaches anyone who stays on
        // the page without clicking anything.
        window.setTimeout(() => {
          if (isPaidRef.current) return;
          if (shouldAskExitSurvey("post_apply_hesitation")) setAskHesitation(true);
        }, 12000);
      }
      // `smooth` depends on rAF, which browsers throttle on the exact
      // hidden->visible transition this fires on. `instant` applies the
      // scroll synchronously, so the checks below see the settled position.
      rootRef.current?.scrollIntoView({ behavior: "instant", block: "center" });

      const el = rootRef.current;
      if (!el) return;

      // Real input after `survey_seen` — distinguishes "looked at the screen"
      // from "the tab regained focus and scrollIntoView ran," which the seen
      // check alone cannot (see the event's own doc comment in events.ts).
      const ENGAGEMENT_EVENTS = ["mousemove", "scroll", "keydown", "touchstart", "click"] as const;
      let engagedFired = false;
      const markEngaged = () => {
        if (engagedFired) return;
        engagedFired = true;
        capture("post_apply_engaged", { job_id: jobId, org_key: orgKey });
        ENGAGEMENT_EVENTS.forEach((ev) => window.removeEventListener(ev, markEngaged));
      };

      let seenFired = false;
      const markSeen = () => {
        if (seenFired) return;
        seenFired = true;
        capture("post_apply_survey_seen", { job_id: jobId, org_key: orgKey });
        ENGAGEMENT_EVENTS.forEach((ev) =>
          window.addEventListener(ev, markEngaged, { passive: true }),
        );
      };

      // WebKit pauses IntersectionObserver in a just-backgrounded tab until a
      // real paint tick, so rAF is a second, independent "we painted" check.
      let rafId = 0;
      const checkRectVisible = () => {
        const rect = el.getBoundingClientRect();
        const inViewport =
          rect.bottom > 0 &&
          rect.top < (window.innerHeight || document.documentElement.clientHeight);
        if (inViewport) markSeen();
        else rafId = window.requestAnimationFrame(checkRectVisible);
      };
      rafId = window.requestAnimationFrame(checkRectVisible);

      let observer: IntersectionObserver | undefined;
      if (typeof IntersectionObserver !== "undefined") {
        observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) markSeen();
          },
          { threshold: 0.1 },
        );
        observer.observe(el);
      }
      window.setTimeout(() => {
        observer?.disconnect();
        window.cancelAnimationFrame(rafId);
        ENGAGEMENT_EVENTS.forEach((ev) => window.removeEventListener(ev, markEngaged));
      }, 8000);
    }

    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [applied, jobId, orgKey]);

  if (!applied) return null;

  // Pre-seeds the wizard with the job they just applied to. Anyone without a
  // membership is walled there (UpgradeWall) and anyone signed out is sent to
  // login with this as `next` — so one href serves all three states.
  const createHref =
    `/dashboard/applications/new?jobUrl=${encodeURIComponent(applyUrl)}` +
    `&role=${encodeURIComponent(jobTitle)}&company=${encodeURIComponent(orgName)}`;

  // Some feeds put the whole vacancy notice in the title field — the sibling
  // board rendered a 118-character one into this sentence. Quoted whole it
  // swallows the sentence it sits inside. The link still carries the full title.
  const shortTitle =
    jobTitle.length > 64
      ? `${jobTitle.slice(0, 64).replace(/[\s,–—-]+\S*$/, "")}…`
      : jobTitle;

  return (
    <div
      ref={rootRef}
      className="mt-4 rounded-lg border-2 border-brand-200 bg-brand-50 px-4 py-4 text-sm"
    >
      <p className="font-semibold text-brand-900">
        Your application to {orgName} is on its way.
      </p>
      {/* A UK reader took the paid video pitch, shown straight after Apply, for
          a fee to apply ("Stop scamming people", 2026-10-03). Charging work
          seekers to find work is unlawful there (Employment Agencies Act 1973
          s.6), so say plainly, before the price, that applying is free. */}
      <p className="mt-1 text-[12.5px] text-brand-800">
        Applying is always free. The video application is an optional extra.
      </p>

      {/* Reordered 2026-10-08: 30d of real data (888 views on this surface)
          showed a 2% act rate against 305 post_apply_engaged, split 5
          video_app_cta_click + 1 post_apply_alert_submit — the paid pitch led
          and the free fallback measured just 0.11% buried beneath it. The free
          ask now leads: lower friction, and it is the one QuickReply
          ("Just want the free alert") already tells us some readers want
          anyway. Testing whether leading with it lifts total action rate. */}
      <div className="mt-3">
        {captured ? (
          <p className="text-[13.5px] font-medium text-brand-800">
            {alertResult?.alreadyConfirmed
              ? "That alert is already on — nothing else to do."
              : "Check your inbox — one click switches the alert on."}
          </p>
        ) : (
          <form
            action={alertAction}
            onSubmit={() => capture("post_apply_alert_submit", { job_id: jobId })}
            className="relative"
          >
            <label
              htmlFor="post-apply-alert-email"
              className="block text-[13.5px] font-medium text-brand-900"
            >
              Have {alertScope} come to you — one email, no account, only new
              listings.
            </label>
            <input type="hidden" name="filters" value={alertFilters} />
            <HoneypotField />
            <div className="mt-2 flex flex-wrap gap-2">
              <input
                id="post-apply-alert-email"
                type="email"
                name="email"
                required
                autoComplete="email"
                placeholder="you@example.com"
                className="h-10 min-w-0 flex-1 rounded-md border border-neutral-300 bg-white px-3 text-[15px] text-ink placeholder:text-neutral-400 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
              />
              <button
                type="submit"
                disabled={alertPending}
                className="inline-flex h-10 items-center justify-center rounded-md border border-brand-600 bg-white px-4 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {alertPending ? "Setting up…" : "Email me these"}
              </button>
            </div>
            <label className="mt-2 flex items-start gap-2 text-[12px] leading-relaxed text-neutral-600">
              <input
                type="checkbox"
                name="terms"
                required
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-brand-600 focus:ring-brand-600"
              />
              <span>
                I accept the{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-700 underline underline-offset-2"
                >
                  terms
                </a>{" "}
                and{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-700 underline underline-offset-2"
                >
                  privacy policy
                </a>
                .
              </span>
            </label>
            {alertResult && !alertResult.ok ? (
              <p
                role="alert"
                className="mt-2 rounded-md border border-stamp-100 bg-stamp-50 px-3 py-2 text-[13px] text-stamp-700"
              >
                {alertResult.message}
              </p>
            ) : null}
          </form>
        )}
      </div>

      <div className="mt-3 rounded-md border border-brand-200 bg-white px-3.5 py-3">
        <p className="text-[13.5px] leading-relaxed text-neutral-700">
          Make the next one impossible to ignore: a{" "}
          <span className="font-semibold text-ink">video application</span>{" "}
          written for{" "}
          <span className="font-medium text-ink">{shortTitle}</span> — we match
          your profile to the description, hand you the talking points, and you
          record a short video to attach to the cover letter.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          <Link
            href={createHref}
            onClick={() =>
              capture("video_app_cta_click", { job_id: jobId })
            }
            className="inline-flex h-10 items-center justify-center rounded-md bg-brand-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
          >
            Create a video application →
          </Link>
          {isPaid ? null : (
            <span className="text-[12.5px] text-neutral-500">
              Premium · {PREMIUM_PRICE_LABEL}, {PREMIUM_BILLING_NOTE}
            </span>
          )}
        </div>
        {isPaid ? null : (
          <div className="mt-2.5">
            {/* T-284 (2026-10-09): matched dashboard's premium_pitch_dismissed
                fix — quickReplies still needed a Send click after picking one;
                this kind sat at 0 feedback_opened too. oneClickOptions submits
                on the first click. */}
            <FeedbackLink
              kind="video_app_pitch_dismissed"
              prompt="Not for you? Tell us why"
              placeholder="What would make a video application worth it — or not — for you?"
              context={`surface: job_post_apply, job_id: ${jobId}`}
              oneClickOptions={[
                "Not comfortable on camera",
                "Don't want to pay for this",
                "Just want the free alert",
              ]}
              className="text-[11.5px] text-neutral-500"
            />
          </div>
        )}
      </div>

      {/* T-246 asked for this question beside the offer: the people who see it
          and do nothing are the only ones who can say why.
          2026-09-29: 0 feedback_opened for this kind (or video_app_pitch_dismissed,
          or dashboard's premium_pitch_dismissed) in 30 days against hundreds of
          pitch views — same low-contrast trap ReportIssueLink had before its fix
          (text-neutral-500 + a static underline, not hover-only). Matched that
          proven fix here instead of touching the offer copy itself. */}
      <div className="mt-3">
        <FeedbackLink
          kind="post_apply_offer"
          prompt="What would make this worth it?"
          placeholder="e.g. too expensive, I'd never record a video, I only need one application…"
          context={`job: ${jobTitle} @ ${orgName}`}
          className="text-[11.5px] text-neutral-500 underline underline-offset-2 hover:text-brand-700"
        />
      </div>

      {askHesitation && !isPaid ? (
        <MicroSurvey
          surveyId="post_apply_hesitation"
          question="Still deciding on the video application? What's holding you back?"
          placeholder="e.g. not comfortable on camera, the price, just not sure it helps…"
          quickOption={{ label: "Not comfortable on camera", answer: "Not comfortable on camera" }}
          onClose={() => setAskHesitation(false)}
        />
      ) : null}
    </div>
  );
}
